import React, { createContext, useContext, useState, useCallback } from "react";

const PopupContext = createContext(null);

export function PopupProvider({ children }) {
  const [activePopup, setActivePopup] = useState(null); // e.g., 'exitIntent', 'timer', 'scroll'
  const [leadCaptured, setLeadCaptured] = useState(
    localStorage.getItem("leadCaptured") === "true"
  );

  /**
   * ✅ Decide if popup can show:
   * - Email popups → block if leadCaptured=true
   * - Non-email popups → ignore leadCaptured
   */
  const requestShow = useCallback(
    (popupName, isLeadPopup = true) => {
      // console.log(`📩 [POPUP REQUESTED] ${popupName} (isLeadPopup = ${isLeadPopup})`);

      if (!activePopup) {
        if (isLeadPopup && leadCaptured) {
          // console.log(`🚫 Skipping popup: ${popupName} — lead already captured`);
          return;
        }
        // console.log(`✅ Showing popup: ${popupName}`);
        setActivePopup(popupName);
      } else {
        // console.log(`⚠️ Popup request ignored: ${popupName} (activePopup = ${activePopup})`);
      }
    },
    [activePopup, leadCaptured]
  );

  /**
   * ✅ Close popup but do NOT persist if dismissed (No Thanks)
   */
  const dismissPopup = useCallback(
    (popupName) => {
      if (activePopup === popupName) {
        // console.log(`🛑 Dismissing popup: ${popupName}`);
        setActivePopup(null);
      }
    },
    [activePopup]
  );

  /**
   * ✅ When lead captured: persist in localStorage and block all future email popups
   */
  const markLeadCaptured = useCallback(() => {
    // console.log("🎯 Lead captured! Blocking all future email popups.");
    setLeadCaptured(true);
    localStorage.setItem("leadCaptured", "true");
    setActivePopup(null);
  }, []);

  return (
    <PopupContext.Provider
      value={{
        activePopup,
        requestShow, // call with (popupName, isLeadPopup)
        dismissPopup,
        markLeadCaptured,
        leadCaptured
      }}
    >
      {children}
    </PopupContext.Provider>
  );
}

export function usePopupController() {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error("usePopupController must be used within a PopupProvider");
  }
  return context;
}